import React, { useState } from "react";
import {
  PaymentPageContainer,
  ContentWrapper,
  HeaderContainer,
  Title,
} from "./PaymentPage.styles";
import Sidebar from "../../components/Sidebar";
import Table, { TableText } from "../../components/Table";
import TableMetrics from "../../components/TableMetrics";
import { Payment } from "../../services/paymentsService/paymentsService.types";
import { fetchPayments } from "../../services/paymentsService/paymentsService";
import { formatPrice } from "../../utils/formatNumber";

import { useListPage } from "hooks/useListPage";

const PaymentPage: React.FC = () => {
  const [totalPayments, setTotalPayments] = useState<number>(0);
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [filters, setFilters] = useState<Record<string, string[]>>({});
  const [payerOptions, setPayerOptions] = useState<Array<{ label: string; value: string }>>([]);
  const filterKey = JSON.stringify(filters);

  const {
    items: payments,
    loading,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    handleSort,
  } = useListPage<Payment, Awaited<ReturnType<typeof fetchPayments>>>({
    fetcher: ({ page, pageSize: size, orderBy }) =>
      fetchPayments(page, size, orderBy, {
        studentIds: filters.student ?? [],
      }),
    select: (response) => ({
      items: response.results.payments,
      total: response.results.aggregated_data.total_payments,
    }),
    onLoaded: (response) => {
      setTotalPayments(response.results.aggregated_data.total_payments);
      setTotalAmount(response.results.aggregated_data.total_amount);
      setPayerOptions(response.results.filter_options?.students ?? []);
    },
    errorMessage: "Ошибка загрузки платежей",
    reloadKey: filterKey,
  });

  const handleFilterSave = (key: string, selected: string[]) => {
    setFilters((previous) => ({ ...previous, [key]: selected }));
    setCurrentPage(1);
    setActiveFilter(null);
  };

  const handleFilterReset = (key: string) => {
    setFilters((previous) => {
      const next = { ...previous };
      delete next[key];
      return next;
    });
    setCurrentPage(1);
    setActiveFilter(null);
  };

  const headers = [
    {
      label: "Плательщик",
      key: "student",
      fieldName: "student__full_name",
      isFilter: true,
      filterType: "list" as const,
      listOptions: payerOptions,
      listFilter: {
        title: "Плательщик",
        searchable: true,
        searchPlaceholder: "Найти плательщика",
        emptyMessage: "Плательщики не найдены",
      },
    },
    { label: "Дата", key: "created", fieldName: "created" },
    { label: "Сумма", key: "price", fieldName: "course__price" },
  ];

  const rowAmountOptions = [
    { label: "7 платежей", value: 7 },
    { label: "14 платежей", value: 14 },
    { label: "20 платежей", value: 20 },
  ];

  const rows = payments.map((payment) => ({
    student: <TableText weight={500}>{payment.student}</TableText>,
    created: new Date(payment.created).toLocaleDateString(),
    price: `${formatPrice(payment.price)} руб.`,
    subscription_end: new Date(payment.subscription_end).toLocaleDateString(),
  }));

  return (
    <PaymentPageContainer>
      <Sidebar />
      <ContentWrapper>
        <HeaderContainer>
          <Title>История платежей</Title>
          <TableMetrics
            items={[
              { label: "Всего платежей", value: formatPrice(totalPayments) },
              { label: "Общая сумма", value: `${formatPrice(totalAmount)} руб.` },
            ]}
          />
        </HeaderContainer>
        <Table
          emptyState={{
            title: "Платежей пока нет",
            description: "Здесь появятся оплаты по вашим курсам.",
          }}
          headers={headers}
          rows={rows}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          onSort={handleSort}
          rowAmountOptions={rowAmountOptions}
          minHeight="230px"
          loading={loading}
          activeFilter={activeFilter}
          handleActiveFilterChange={setActiveFilter}
          appliedFilters={filters}
          onFilterSave={handleFilterSave}
          onFilterReset={handleFilterReset}
        />
      </ContentWrapper>
    </PaymentPageContainer>
  );
};

export default PaymentPage;
